import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert } from '@mui/material';
import {
  bulkProductService,
  BulkProduct,
  CreateBulkProductData,
  BloomStockItem,
  BloomStockItemKind,
} from '../services/bulkProductService';
import { format_utc_instant_date } from '../utils/shopTimezone';
import {
  parseBulkProductsTab,
  parseBloomStockFilter,
  formatBloomStockImportNotice,
  type BulkProductsTab,
} from './bulkProducts.helpers';

const emptyStockForm = {
  name: '',
  sku_number: '',
  vendor: '',
  item_kind: 'ingredient' as BloomStockItemKind,
  low_stock_threshold_cases: 0,
  initial_cases: 0,
};

const BulkProducts: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseBulkProductsTab(searchParams.get('tab'));
  const stockFilter = parseBloomStockFilter(searchParams.get('filter'));

  const [bulkProducts, setBulkProducts] = useState<BulkProduct[]>([]);
  const [stockItems, setStockItems] = useState<BloomStockItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<BulkProduct | null>(null);
  const [editingStock, setEditingStock] = useState<BloomStockItem | null>(null);
  const [modalError, setModalError] = useState('');
  const [stockForm, setStockForm] = useState(emptyStockForm);
  const [adjustingItem, setAdjustingItem] = useState<BloomStockItem | null>(null);
  const [adjustDirection, setAdjustDirection] = useState<'add' | 'reduce'>('add');
  const [adjustQuantity, setAdjustQuantity] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [importNotice, setImportNotice] = useState<{
    severity: 'success' | 'error';
    message: string;
  } | null>(null);
  const importInputRef = React.useRef<HTMLInputElement | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  const abortControllerRef = React.useRef<AbortController | null>(null);
  const [formData, setFormData] = useState<CreateBulkProductData>({
    name: '',
    sku_number: '',
    vendor: '',
  });

  useEffect(() => {
    const urlPage = parseInt(searchParams.get('page') || '1');
    const urlPageSize = parseInt(searchParams.get('pageSize') || '20');
    const urlSearch = searchParams.get('search') || '';
    setCurrentPage(urlPage < 1 ? 1 : urlPage);
    setPageSize(urlPageSize < 1 ? 20 : urlPageSize);
    setSearchTerm(urlSearch);
    setDebouncedSearchTerm(urlSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (currentPage > 0 && pageSize > 0) {
      if (tab === 'stock') {
        loadBloomStock();
      } else {
        loadBulkProducts();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, debouncedSearchTerm, tab, stockFilter]);

  useEffect(() => {
    if (currentPage > 0 && pageSize > 0) {
      const params = new URLSearchParams(searchParams);
      params.set('tab', tab);
      params.set('page', currentPage.toString());
      params.set('pageSize', pageSize.toString());
      if (searchTerm) {
        params.set('search', searchTerm);
      } else {
        params.delete('search');
      }
      if (tab === 'stock' && stockFilter === 'low') {
        params.set('filter', 'low');
      } else {
        params.delete('filter');
      }
      setSearchParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, searchTerm, tab, stockFilter]);

  const setTab = (next: BulkProductsTab) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    params.set('page', '1');
    if (next !== 'stock') {
      params.delete('filter');
    }
    setCurrentPage(1);
    setSearchParams(params);
  };

  const setStockFilter = (next: 'all' | 'low') => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', 'stock');
    params.set('page', '1');
    if (next === 'low') {
      params.set('filter', 'low');
    } else {
      params.delete('filter');
    }
    setCurrentPage(1);
    setSearchParams(params);
  };

  const apiErrorMessage = (err: any) =>
    err.response?.data?.error?.message || err.message || 'An error occurred';

  const applyPagination = (total_pages: number, total: number, page: number) => {
    setTotalPages(total_pages);
    setTotalItems(total);
    if (page > total_pages && total_pages > 0) {
      setCurrentPage(1);
    }
  };

  const withListRequest = async (run: (signal: AbortSignal) => Promise<void>) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    try {
      setLoading(true);
      setError('');
      await run(abortController.signal);
    } catch (err: any) {
      if (err.name === 'AbortError' || abortController.signal.aborted) {
        return;
      }
      setError(err.message);
    } finally {
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    }
  };

  const loadBulkProducts = async (page = currentPage, size = pageSize) => {
    await withListRequest(async (signal) => {
      const result = await bulkProductService.getBulkProductsPaginated(
        page,
        size,
        debouncedSearchTerm.trim() || undefined
      );
      if (signal.aborted) return;
      setBulkProducts(result.bulkProducts);
      applyPagination(result.pagination.totalPages, result.pagination.total, page);
    });
  };

  const loadBloomStock = async (page = currentPage, size = pageSize) => {
    await withListRequest(async (signal) => {
      const result = await bulkProductService.getBloomStockPaginated(
        page,
        size,
        debouncedSearchTerm.trim() || undefined,
        stockFilter
      );
      if (signal.aborted) return;
      setStockItems(result.items);
      applyPagination(result.pagination.totalPages, result.pagination.total, page);
    });
  };

  const reloadCurrentTab = async () => {
    if (tab === 'stock') {
      await loadBloomStock();
    } else {
      await loadBulkProducts();
    }
  };

  const handleSearch = async () => {
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number) => {
    const validPage = Math.max(1, Math.min(newPage, totalPages || 1));
    setCurrentPage(validPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setModalError('');
      setError('');
      if (editingProduct) {
        await bulkProductService.updateBulkProduct(editingProduct.id, formData);
      } else {
        await bulkProductService.createBulkProduct(formData);
      }
      setShowForm(false);
      setEditingProduct(null);
      setFormData({ name: '', sku_number: '', vendor: '' });
      await loadBulkProducts();
    } catch (err: any) {
      setModalError(apiErrorMessage(err));
    }
  };

  const handleStockSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setModalError('');
      setError('');
      if (editingStock) {
        await bulkProductService.updateBloomStock(editingStock.id, {
          name: stockForm.name,
          sku_number: stockForm.sku_number,
          vendor: stockForm.vendor,
          item_kind: stockForm.item_kind,
          low_stock_threshold_cases: Number(stockForm.low_stock_threshold_cases) || 0,
        });
      } else {
        await bulkProductService.createBloomStock({
          name: stockForm.name,
          sku_number: stockForm.sku_number,
          vendor: stockForm.vendor,
          item_kind: stockForm.item_kind,
          low_stock_threshold_cases: Number(stockForm.low_stock_threshold_cases) || 0,
          initial_cases: Number(stockForm.initial_cases) || 0,
        });
      }
      setShowForm(false);
      setEditingStock(null);
      setStockForm(emptyStockForm);
      await loadBloomStock();
    } catch (err: any) {
      setModalError(apiErrorMessage(err));
    }
  };

  const handleEdit = (product: BulkProduct) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      sku_number: product.sku_number,
      vendor: product.vendor || '',
    });
    setShowForm(true);
  };

  const handleEditStock = (item: BloomStockItem) => {
    setEditingStock(item);
    setStockForm({
      name: item.name,
      sku_number: item.sku_number,
      vendor: item.vendor || '',
      item_kind: item.item_kind,
      low_stock_threshold_cases: item.low_stock_threshold_cases,
      initial_cases: 0,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (
      !confirm(
        'Delete this bulk product? If it has Bloom stock, the count will be reduced to 0 and the ledger will be kept (records are not deleted).'
      )
    ) {
      return;
    }

    try {
      setError('');
      const message = await bulkProductService.deleteBulkProduct(id);
      setImportNotice({ severity: 'success', message });
      await reloadCurrentTab();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const openAdjust = (item: BloomStockItem, direction: 'add' | 'reduce') => {
    setAdjustingItem(item);
    setAdjustDirection(direction);
    setAdjustQuantity('');
    setAdjustReason('');
    setModalError('');
  };

  const handleAdjustSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!adjustingItem) return;
    const quantity = parseInt(adjustQuantity, 10);
    if (!Number.isInteger(quantity) || quantity <= 0) {
      setModalError('Enter a whole number of cases greater than 0');
      return;
    }
    const cases_adjusted = adjustDirection === 'add' ? quantity : -quantity;
    try {
      setModalError('');
      await bulkProductService.adjustBloomStock(
        adjustingItem.id,
        cases_adjusted,
        adjustReason || undefined
      );
      setAdjustingItem(null);
      await loadBloomStock();
    } catch (err: any) {
      setModalError(apiErrorMessage(err));
    }
  };

  const handleImportFile = async (file: File) => {
    setImportNotice(null);
    const reader = new FileReader();
    reader.onerror = () => {
      setImportNotice({ severity: 'error', message: 'Could not read the import file.' });
    };
    reader.onload = async () => {
      try {
        const result = String(reader.result || '');
        const base64 = result.includes(',') ? result.split(',')[1] : result;
        const importResult = await bulkProductService.importBloomStock(base64, file.name);
        const notice = formatBloomStockImportNotice({
          applied_count: importResult.applied.length,
          errors: importResult.errors,
        });
        await loadBloomStock();
        setImportNotice(notice);
      } catch (err: any) {
        setImportNotice({
          severity: 'error',
          message: apiErrorMessage(err) || 'Import failed',
        });
      }
    };
    reader.readAsDataURL(file);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingProduct(null);
    setEditingStock(null);
    setFormData({ name: '', sku_number: '', vendor: '' });
    setStockForm(emptyStockForm);
    setError('');
    setModalError('');
  };

  const kindLabel = (kind: string) => {
    if (kind === 'box') return 'Box';
    if (kind === 'container') return 'Container';
    return 'Ingredient';
  };

  const paginationBar = (
    <>
      {totalPages > 1 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '1rem',
            padding: '1rem 0',
            borderTop: '1px solid #e1e5e9',
          }}
        >
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            Showing {(currentPage - 1) * pageSize + 1} to{' '}
            {Math.min(currentPage * pageSize, totalItems)} of {totalItems}{' '}
            {tab === 'stock' ? 'Bloom stock items' : 'bulk products'}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label style={{ fontSize: '0.875rem', color: '#6b7280' }}>Show:</label>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                style={{
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.875rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                }}
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.875rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  backgroundColor: currentPage === 1 ? '#f9fafb' : 'white',
                  color: currentPage === 1 ? '#9ca3af' : '#374151',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                }}
              >
                Previous
              </button>

              <span style={{ fontSize: '0.875rem', color: '#6b7280', padding: '0 0.5rem' }}>
                Page {currentPage} of {totalPages}
              </span>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.875rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  backgroundColor: currentPage === totalPages ? '#f9fafb' : 'white',
                  color: currentPage === totalPages ? '#9ca3af' : '#374151',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                }}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Bulk Products</h1>
        <p className="page-subtitle">
          {tab === 'stock'
            ? 'Count of bulk Bloom buys from vendors (ingredients, boxes, containers)'
            : 'Pick-list of raw materials and bulk ingredients'}
        </p>
      </div>

      <div className="tabs" style={{ marginBottom: '1rem', borderRadius: '8px' }}>
        <button
          type="button"
          className={`tab${tab === 'items' ? ' active' : ''}`}
          onClick={() => setTab('items')}
        >
          Bulk items
        </button>
        <button
          type="button"
          className={`tab${tab === 'stock' ? ' active' : ''}`}
          onClick={() => setTab('stock')}
        >
          Bloom stock
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {importNotice && (
        <Alert severity={importNotice.severity} sx={{ mb: 2 }} onClose={() => setImportNotice(null)}>
          {importNotice.message}
        </Alert>
      )}

      {tab === 'items' && (
        <div className="card">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
            }}
          >
            <h3>Bulk Product Management</h3>
            <button className="btn btn-primary" onClick={() => setShowForm(true)}>
              Add New Bulk Product
            </button>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="text"
                placeholder="Search bulk products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                style={{ flex: 1, padding: '0.5rem' }}
              />
              <button className="btn btn-secondary" onClick={handleSearch}>
                Search
              </button>
              {searchTerm && (
                <button
                  className="btn btn-outline"
                  onClick={() => {
                    setSearchTerm('');
                    loadBulkProducts();
                  }}
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>Loading bulk products...</div>
          ) : (
            <>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>SKU Number</th>
                      <th>Vendor</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkProducts.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>
                          No bulk products found
                        </td>
                      </tr>
                    ) : (
                      bulkProducts.map((product) => (
                        <tr key={product.id}>
                          <td>{product.name}</td>
                          <td>{product.sku_number}</td>
                          <td>{product.vendor || '-'}</td>
                          <td>{format_utc_instant_date(product.created_at)}</td>
                          <td>
                            <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                              <button
                                className="btn btn-small"
                                onClick={() => handleEdit(product)}
                                title="Edit Bulk Product"
                                style={{
                                  padding: '0.375rem 0.75rem',
                                  backgroundColor: '#f8f9fa',
                                  border: '1px solid #dee2e6',
                                  borderRadius: '4px',
                                  color: '#495057',
                                  fontSize: '0.875rem',
                                  fontWeight: '500',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.25rem',
                                  cursor: 'pointer',
                                }}
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-small"
                                onClick={() => handleDelete(product.id)}
                                title="Delete Bulk Product"
                                style={{
                                  padding: '0.375rem 0.75rem',
                                  backgroundColor: '#fff5f5',
                                  border: '1px solid #fed7d7',
                                  borderRadius: '4px',
                                  color: '#c53030',
                                  fontSize: '0.875rem',
                                  fontWeight: '500',
                                  cursor: 'pointer',
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {paginationBar}
            </>
          )}
        </div>
      )}

      {tab === 'stock' && (
        <div className="card">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
              gap: '0.75rem',
              flexWrap: 'wrap',
            }}
          >
            <h3>Bloom stock</h3>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <input
                ref={importInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleImportFile(file);
                    e.target.value = '';
                  }
                }}
              />
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => importInputRef.current?.click()}
              >
                Import
              </button>
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => {
                  setEditingStock(null);
                  setStockForm(emptyStockForm);
                  setShowForm(true);
                }}
              >
                Add Bloom stock
              </button>
            </div>
          </div>

          <p style={{ color: '#6b7280', marginTop: 0 }}>
            Counts change only from this tab (type or spreadsheet). Completing a production order
            does not change these numbers.
          </p>

          <div style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="Search Bloom stock..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                style={{ flex: 1, padding: '0.5rem', minWidth: '12rem' }}
              />
              <button
                className={`btn ${stockFilter === 'low' ? 'btn-primary' : 'btn-outline'}`}
                type="button"
                onClick={() => setStockFilter(stockFilter === 'low' ? 'all' : 'low')}
              >
                {stockFilter === 'low' ? 'Showing low stock' : 'Low stock only'}
              </button>
              {searchTerm && (
                <button className="btn btn-outline" type="button" onClick={() => setSearchTerm('')}>
                  Clear
                </button>
              )}
            </div>
            <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.5rem' }}>
              Spreadsheet columns: sku_number, cases_adjusted (or quantity_delta), reason (CSV or
              Excel). Positive adds cases; negative reduces. The whole file is validated first;
              nothing is imported if any row is wrong.
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>Loading Bloom stock...</div>
          ) : (
            <>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>SKU</th>
                      <th>Vendor</th>
                      <th>Type</th>
                      <th>On hand</th>
                      <th>Reorder when below</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockItems.length === 0 ? (
                      <tr>
                        <td colSpan={8} style={{ textAlign: 'center', padding: '2rem' }}>
                          {stockFilter === 'low'
                            ? 'No Bloom stock items are below their reorder number'
                            : 'No Bloom stock items yet'}
                        </td>
                      </tr>
                    ) : (
                      stockItems.map((item) => (
                        <tr
                          key={item.id}
                          style={item.is_low ? { backgroundColor: '#fff5f5' } : undefined}
                        >
                          <td>{item.name}</td>
                          <td>{item.sku_number}</td>
                          <td>{item.vendor || '-'}</td>
                          <td>{kindLabel(item.item_kind)}</td>
                          <td>{item.cases_on_hand}</td>
                          <td>
                            {item.low_stock_threshold_cases > 0
                              ? item.low_stock_threshold_cases
                              : '—'}
                          </td>
                          <td>
                            {item.is_low ? (
                              <span style={{ color: '#c53030', fontWeight: 600 }}>Low</span>
                            ) : (
                              'OK'
                            )}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                              <button
                                className="btn btn-small"
                                type="button"
                                onClick={() => openAdjust(item, 'add')}
                              >
                                Add
                              </button>
                              <button
                                className="btn btn-small"
                                type="button"
                                onClick={() => openAdjust(item, 'reduce')}
                              >
                                Reduce
                              </button>
                              <button
                                className="btn btn-small"
                                type="button"
                                onClick={() => handleEditStock(item)}
                              >
                                Edit
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {paginationBar}
            </>
          )}
        </div>
      )}

      {showForm && tab === 'items' && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{editingProduct ? 'Edit Bulk Product' : 'Add New Bulk Product'}</h3>
              <button className="btn btn-small" onClick={resetForm}>
                ×
              </button>
            </div>
            {modalError && (
              <div
                style={{
                  margin: '1rem 1.5rem',
                  padding: '0.75rem 1rem',
                  backgroundColor: '#fee',
                  border: '1px solid #fcc',
                  borderRadius: '4px',
                  color: '#c33',
                }}
              >
                {modalError}
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  maxLength={100}
                />
              </div>
              <div className="form-group">
                <label htmlFor="sku_number">SKU Number *</label>
                <input
                  type="text"
                  id="sku_number"
                  value={formData.sku_number}
                  onChange={(e) => setFormData({ ...formData, sku_number: e.target.value })}
                  required
                  maxLength={50}
                />
              </div>
              <div className="form-group">
                <label htmlFor="vendor">Vendor</label>
                <input
                  type="text"
                  id="vendor"
                  value={formData.vendor || ''}
                  onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                  maxLength={100}
                  placeholder="Optional vendor name"
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingProduct ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showForm && tab === 'stock' && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{editingStock ? 'Edit Bloom stock' : 'Add Bloom stock'}</h3>
              <button className="btn btn-small" onClick={resetForm}>
                ×
              </button>
            </div>
            {modalError && (
              <div
                style={{
                  margin: '1rem 1.5rem',
                  padding: '0.75rem 1rem',
                  backgroundColor: '#fee',
                  border: '1px solid #fcc',
                  borderRadius: '4px',
                  color: '#c33',
                }}
              >
                {modalError}
              </div>
            )}
            <form onSubmit={handleStockSubmit}>
              <div className="form-group">
                <label htmlFor="stock_name">Name *</label>
                <input
                  type="text"
                  id="stock_name"
                  value={stockForm.name}
                  onChange={(e) => setStockForm({ ...stockForm, name: e.target.value })}
                  required
                  maxLength={100}
                />
              </div>
              <div className="form-group">
                <label htmlFor="stock_sku">SKU Number *</label>
                <input
                  type="text"
                  id="stock_sku"
                  value={stockForm.sku_number}
                  onChange={(e) => setStockForm({ ...stockForm, sku_number: e.target.value })}
                  required
                  maxLength={50}
                />
              </div>
              <div className="form-group">
                <label htmlFor="stock_vendor">Vendor</label>
                <input
                  type="text"
                  id="stock_vendor"
                  value={stockForm.vendor}
                  onChange={(e) => setStockForm({ ...stockForm, vendor: e.target.value })}
                  maxLength={100}
                />
              </div>
              <div className="form-group">
                <label htmlFor="stock_kind">Type *</label>
                <select
                  id="stock_kind"
                  value={stockForm.item_kind}
                  onChange={(e) =>
                    setStockForm({
                      ...stockForm,
                      item_kind: e.target.value as BloomStockItemKind,
                    })
                  }
                  required
                >
                  <option value="ingredient">Ingredient</option>
                  <option value="box">Box</option>
                  <option value="container">Container</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="stock_threshold">Reorder when below (cases)</label>
                <input
                  type="number"
                  id="stock_threshold"
                  min={0}
                  step={1}
                  value={stockForm.low_stock_threshold_cases}
                  onChange={(e) =>
                    setStockForm({
                      ...stockForm,
                      low_stock_threshold_cases: parseInt(e.target.value, 10) || 0,
                    })
                  }
                />
                <small>0 means no low-stock alert.</small>
              </div>
              {!editingStock && (
                <div className="form-group">
                  <label htmlFor="stock_initial">Starting count (cases)</label>
                  <input
                    type="number"
                    id="stock_initial"
                    min={0}
                    step={1}
                    value={stockForm.initial_cases}
                    onChange={(e) =>
                      setStockForm({
                        ...stockForm,
                        initial_cases: parseInt(e.target.value, 10) || 0,
                      })
                    }
                  />
                </div>
              )}
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingStock ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {adjustingItem && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>
                {adjustDirection === 'add' ? 'Add' : 'Reduce'} {adjustingItem.name}
              </h3>
              <button className="btn btn-small" onClick={() => setAdjustingItem(null)}>
                ×
              </button>
            </div>
            {modalError && (
              <div
                style={{
                  margin: '1rem 1.5rem',
                  padding: '0.75rem 1rem',
                  backgroundColor: '#fee',
                  border: '1px solid #fcc',
                  borderRadius: '4px',
                  color: '#c33',
                }}
              >
                {modalError}
              </div>
            )}
            <form onSubmit={handleAdjustSubmit}>
              <p style={{ margin: '0 1.5rem 1rem' }}>
                On hand: <strong>{adjustingItem.cases_on_hand}</strong> cases
                {adjustingItem.low_stock_threshold_cases > 0
                  ? ` · reorder when below ${adjustingItem.low_stock_threshold_cases}`
                  : ''}
              </p>
              <div className="form-group">
                <label htmlFor="adjust_qty">Cases *</label>
                <input
                  type="number"
                  id="adjust_qty"
                  min={1}
                  step={1}
                  value={adjustQuantity}
                  onChange={(e) => setAdjustQuantity(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="adjust_reason">Reason</label>
                <input
                  type="text"
                  id="adjust_reason"
                  value={adjustReason}
                  onChange={(e) => setAdjustReason(e.target.value)}
                  maxLength={255}
                  placeholder="Optional"
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setAdjustingItem(null)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {adjustDirection === 'add' ? 'Add' : 'Reduce'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkProducts;
